import React, { useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  Input,
  Spinner,
  Table
} from 'reactstrap'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

import TablePagination from '../../../components/TablePagination'
import ValueDisplay from './ValueDisplay'

const MissingLocaleTable = ({
  bulkUpdateStatus = 'idle',
  items = [],
  locales = [],
  localesFetchingStatus = 'idle',
  moveToPage,
  onBulkApply,
  onPathSearch,
  paginationCurrentPage = 1,
  paginationTotalPages = 1,
  totalCount = 0
}) => {
  const { t } = useTranslation(['ProjectsPage', 'commons'])
  const [pathFilterInput, setPathFilterInput] = useState('')
  const [activeFilter, setActiveFilter] = useState('')
  const [selectedLocale, setSelectedLocale] = useState(null)

  const missingLocaleItems = items.filter((item) => item.locale === '')

  if (missingLocaleItems.length === 0) {
    return null
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setActiveFilter(pathFilterInput)
    setSelectedLocale(null)
    onPathSearch(pathFilterInput)
  }

  const handleClearFilter = () => {
    setPathFilterInput('')
    setActiveFilter('')
    setSelectedLocale(null)
    onPathSearch('')
  }

  const handleBulkApply = () => {
    if (!selectedLocale || !activeFilter) return
    onBulkApply(activeFilter, selectedLocale.value)
  }

  const isUpdating = bulkUpdateStatus === 'updating'
  const isLocalesLoading = ['idle', 'fetching'].includes(localesFetchingStatus)
  const isFilterActive = activeFilter !== ''

  return (
    <Card className="mb-4">
      <CardHeader>
        <div className="d-flex align-items-center gap-2">
          <span className="fs-5">🏷️</span>
          <span className="fw-semibold">
            {t('ImportIssues.missingLocaleTable.title')}
          </span>
        </div>
      </CardHeader>
      <CardBody>
        {/* Filter bar */}
        <div className="d-flex gap-3 mb-3 align-items-end">
          <div className="flex-grow-1">
            <label
              className="form-label small text-muted"
              htmlFor="path-filter"
            >
              {t('ImportIssues.missingLocaleTable.filterByPath')}
            </label>
            <Form onSubmit={handleSearch}>
              <div className="d-flex gap-2">
                <Input
                  id="path-filter"
                  onChange={(e) => setPathFilterInput(e.target.value)}
                  placeholder={t('ImportIssues.missingLocaleTable.pathPlaceholder')}
                  value={pathFilterInput}
                />
                <Button color="secondary" type="submit">
                  {t('buttons.search', { ns: 'commons' })}
                </Button>
                {isFilterActive && (
                  <Button color="link" onClick={handleClearFilter}>
                    {t('buttons.clear', { ns: 'commons' })}
                  </Button>
                )}
              </div>
            </Form>
          </div>
          {isFilterActive && (
            <>
              <div style={{ minWidth: 200 }}>
                <label
                  className="form-label small text-muted"
                  htmlFor="locale-select"
                >
                  {t('ImportIssues.missingLocaleTable.locale')}
                </label>
                {isLocalesLoading ? (
                  <div className="d-flex align-items-center">
                    <Spinner size="sm" className="me-2" />
                    <span className="text-muted small">
                      {t('fetchingLocales', { ns: 'ProjectsPage' })}
                    </span>
                  </div>
                ) : (
                  <Select
                    id="locale-select"
                    isClearable
                    onChange={setSelectedLocale}
                    options={locales}
                    placeholder={t('ImportIssues.missingLocaleTable.selectLocale')}
                    value={selectedLocale}
                  />
                )}
              </div>
              <Button
                color="primary"
                disabled={!selectedLocale || isUpdating}
                onClick={handleBulkApply}
              >
                {isUpdating && <Spinner size="sm" className="me-1" />}
                {t('ImportIssues.missingLocaleTable.applyToFiltered', {
                  count: totalCount
                })}
              </Button>
            </>
          )}
        </div>

        {/* Table */}
        <div className="table-responsive">
          <Table hover>
            <thead>
              <tr>
                <th>{t('ImportIssues.missingLocaleTable.key')}</th>
                <th>{t('ImportIssues.missingLocaleTable.path')}</th>
                <th>{t('ImportIssues.missingLocaleTable.value')}</th>
              </tr>
            </thead>
            <tbody>
              {missingLocaleItems.map((item) => (
                <tr key={item.id}>
                  <td>
                    <code className="small">{item.key}</code>
                  </td>
                  <td>
                    <code className="small text-muted">{item.path}</code>
                  </td>
                  <td className="text-truncate" style={{ maxWidth: 300 }}>
                    <ValueDisplay value={item.value} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-center mt-3">
          <TablePagination
            currentPage={paginationCurrentPage}
            onPaginationClick={moveToPage}
            totalPages={paginationTotalPages}
          />
        </div>
      </CardBody>
    </Card>
  )
}

MissingLocaleTable.propTypes = {
  bulkUpdateStatus: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    locale: PropTypes.string,
    path: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool,
      PropTypes.number,
      PropTypes.string
    ])
  })),
  locales: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })),
  localesFetchingStatus: PropTypes.string,
  moveToPage: PropTypes.func.isRequired,
  onBulkApply: PropTypes.func.isRequired,
  onPathSearch: PropTypes.func.isRequired,
  paginationCurrentPage: PropTypes.number,
  paginationTotalPages: PropTypes.number,
  totalCount: PropTypes.number
}

export default MissingLocaleTable
